import { get, transform, filter } from 'lodash';
import React from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import moment from 'moment';

import {
    DMARC_NONE,
    DMARC_POLICY_INHERITED_INHERITED,
    DMARC_POLICY_INHERITED_NONE,
    DMARC_POLICY_INHERITED_SP,
    DMARC_STATUS_POLICY_INVALID,
    DMARC_STATUS_POLICY_NONE,
    DMARC_STATUS_POLICY_QUARANTINE,
    DMARC_STATUS_POLICY_REJECT,
    DMARC_QUARANTINE,
    DMARC_REJECT,
    DMARC_STATUS_INVALID,
    DMARC_STATUS_NO_RECORD,
} from '../common/constants/dmarc-policy-values';

import { TABLE_TYPES } from './constants/table-options';
import { generateCorrectFilterKey, generateCorrectQueryParam } from './filter-utils';
import { iconMap } from '../common/constants/ui-options';
import { translate } from '../utils/translation-utils';
import * as commonUtils from '../utils/common-utils';
import opacity from '../common/theme/colors/opacity';

export const formatFilename = ( filename = 'download' , fileExtension = 'txt', includeTimestamp = false ) => {
    let date = '';
    if( includeTimestamp ){
        date = `-${moment().unix()}`;
    }

    return `${filename}${date}.${fileExtension}`;
};

// Formatters
export const getCorrectParamValue = ( param ) => {
    if( param === undefined ){
        //Parameters should not be undefined but we don't want it to be a breaking change if they are.
        console.warn( 'You are passing in an undefined parameter. Please verify your data!' );
        return '';
    }
    //Due to all of the different data coming back from aggFuncs and groupedRowAggNodes
    //we need to ensure that we are grabbing the correct values
    let paramValue = param;

    if( param.node ){
        if( param.value && typeof param.value === 'object' && !Array.isArray( param ) && param.value.node ){
            paramValue = param.node.aggData ? param.node.aggData[param.colDef.field] : 0;
        }
        else{
            if( paramValue && paramValue.value !== undefined ){
                paramValue = param.value;
            }
            else{
                paramValue = param.node.aggData ? param.node.aggData[param.colDef.field] : 0;
            }

        }
    }
    else if( param.value ){
        paramValue = param.value;
    }

    return paramValue;
};

export const boolFormatter = ( { value } ) => commonUtils.formatBool( value );

export const commaSeparatedListFormatter = ( { value } ) => commonUtils.formatCommaSeparatedList( value );

export const dateFormatter = ( { value } ) => commonUtils.formatDate( value );

export const numberFormatter = ( param ) => commonUtils.formatNumber( getCorrectParamValue( param ) );

export const rateFormatter = ( param ) => commonUtils.formatRate( getCorrectParamValue( param ) );

export const stringFormatter = ( param ) => get( param, 'value', '' );


// Renderers
export const translateStringRenderer = ( { value } ) => translate( value );

export const dmarcPolicyIconRenderer = ( cell ) => {
    let Icon = null;
    let tooltipText;
    let SecondaryIcon = null;
    let secondaryTooltipText;

    const styles = { color: opacity.black38, marginTop: 12 };
    const policyInherited = get( cell, 'data.dmarcPolicyInherited', DMARC_POLICY_INHERITED_NONE );

    if( 'value' in cell ){
        Icon = iconMap[cell.value].icon;
        tooltipText = iconMap[cell.value].tooltip;

        if( policyInherited === DMARC_POLICY_INHERITED_INHERITED ){
            SecondaryIcon = iconMap.policyInherited.icon;
            secondaryTooltipText = iconMap.policyInherited.tooltip;
        }

        if( policyInherited === DMARC_POLICY_INHERITED_SP ){
            SecondaryIcon = iconMap.subdomainPolicy.icon;
            secondaryTooltipText = iconMap.subdomainPolicy.tooltip;
        }
    }

    return (
        <>
            {
                Icon &&
                ( <Tooltip title={tooltipText}>
                    <Icon style={styles} />
                </Tooltip> )
            }
            {
                SecondaryIcon &&
                ( <Tooltip title={secondaryTooltipText}>
                    <SecondaryIcon style={{ ...styles, marginLeft: 8 }} />
                </Tooltip> )
            }
        </>
    );
};

export const dmarcPolicyFilterRenderer = ( { value } ) => {
    let display;

    switch( value ){
        case DMARC_STATUS_POLICY_INVALID:
            display = 'Invalid record';
            break;
        case DMARC_STATUS_NO_RECORD:
            display = 'No record';
            break;
        case DMARC_STATUS_POLICY_NONE:
            display = 'p=none';
            break;
        case DMARC_STATUS_POLICY_QUARANTINE:
            display = 'p=quarantine';
            break;
        case DMARC_STATUS_POLICY_REJECT:
            display = 'p=reject';
            break;
        default:
            display = value;
    }

    return display;
};

export const dmarcSubdomainPolicyFilterRenderer = ( { value } ) => {
    let display;

    switch( value ){
        case null:
            display = 'No subdomain policy';
            break;
        case DMARC_NONE:
            display = 'sp=none';
            break;
        case DMARC_QUARANTINE:
            display = 'sp=quarantine';
            break;
        case DMARC_REJECT:
            display = 'sp=reject';
            break;
        default:
            display = value;
    }

    return display;
};


// Comparators
export const dmarcPolicyCalculator = ( dmarcPolicy, policyInherited ) => {
    let policyInt;

    switch( dmarcPolicy ){
        case DMARC_STATUS_NO_RECORD:
            policyInt = 6;
            break;
        case DMARC_STATUS_INVALID:
            policyInt = 11;
            break;
        case DMARC_NONE:
            policyInt = 21;
            break;
        case DMARC_QUARANTINE:
            policyInt = 31;
            break;
        case DMARC_REJECT:
            policyInt = 41;
            break;
        default:
            policyInt = 0;
            break;
    }

    if( policyInherited === DMARC_POLICY_INHERITED_INHERITED ){
        policyInt--;
    }
    return policyInt;
};


// Handlers
export const backToTopHandler = ( top, hasScrolledHandler, scrollToTopHandler ) => {
    if( top !== 0 ){
        hasScrolledHandler( true );
    }
    else{
        hasScrolledHandler( false );
        scrollToTopHandler( false );
    }
};

const checkForUnselectAllFilter = ( filteredColumns, filterModel ) => {
    if( !filteredColumns.length ){
        return false;
    }
    /*
        The selectFilters are the only ones that are an array, the rest of the filters are objects. We are getting
        any select filters that have no values checked.  If so then the ag-grid will filter it out even though
        we do have the data from the server.
    */
    const SelectFiltersWithNoValues = filter( filterModel, column  =>
        Array.isArray( column.values ) && !column.values.length
    );

    return SelectFiltersWithNoValues.length !== 0;
};

const generateCorrectKeysAndValuesForQuery = ( filterModel ) => {
    return transform( filterModel, ( result, filter, column ) => {
        if( filter.clear ){
            return;
        }

        const correctFilterKey = generateCorrectFilterKey( filter, column );
        const formattedFilterResult = generateCorrectQueryParam ( filter, column );

        result[correctFilterKey] = formattedFilterResult;
    } );
};

// Server Side Handlers
export const serverFilterHandler = ( api, filterHandler ) => {
    api.deselectAll();

    const filterModel = api.getFilterModel();
    const filteredColumns = Object.keys( filterModel );

    if( filteredColumns ){

        const columnFilterPairs = generateCorrectKeysAndValuesForQuery( filterModel );

        const noVisibleRows = checkForUnselectAllFilter( filteredColumns, filterModel );

        filterHandler( columnFilterPairs, noVisibleRows );
    }
};

export const serverSortHandler = ( api, sortHandler ) => {
    const sortModel = api.getSortModel()[0];

    const sortedColumn = get( sortModel, 'colId', null );
    const direction = get( sortModel, 'sort', null );

    if( sortedColumn ){
        api.deselectAll();
        sortHandler( { orderBy: sortedColumn, orderDir: direction } );
    }
};

export const serverPaginationHandler = ( api, paginateHandler ) => {
    const totalRowCount =  api.getDisplayedRowCount();
    const lastDisplayedRow = api.getLastDisplayedRow() + 1;
    const rowsFromBottom = totalRowCount - lastDisplayedRow;

    const nearBottomOfTable = rowsFromBottom >= 0 && rowsFromBottom < 10;

    if( nearBottomOfTable && totalRowCount >= 50 ){
        paginateHandler();
    }
};

export const activeClass = ( isActive ) => {
    if( isActive ){
        return TABLE_TYPES.ACTIVE;
    }
    return '';
};

